/**
 * Accès client au flux public d'événements + formatage partagé.
 *
 * Le backend fusionne déjà la table `events` (publiés) et l'historique
 * `agenda.json` : côté front il n'y a plus qu'UNE source, `/events/public/...`,
 * consommée à l'identique par la home, l'agenda et la page événement.
 *
 * Toutes ces routes sont publiques (`auth: false`) : la page événement doit
 * s'ouvrir depuis un lien partagé, sans compte.
 */
import { apiFetch, BACKEND_URL, TENANT_ID } from './api';

export interface PublicEvent {
  id: string;
  /** 'events' = créé par le staff · 'agenda' = hérité du blob agenda.json. */
  source: 'events' | 'agenda';
  tenantId: string;
  title: string;
  type: string;
  description: string | null;
  /** 'YYYY-MM-DD' */
  date: string;
  dateEnd: string | null;
  /** 'HH:MM' */
  startTime: string | null;
  endTime: string | null;
  coverImageUrl: string | null;
  capacity: number | null;
  /** CENTIMES d'euro. null ou 0 = entrée libre. */
  price: number | null;
}

export interface PublicEventTenant {
  id: string;
  name: string;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
}

/**
 * Agenda public du tenant courant, trié par date croissante.
 * Retourne `[]` sur erreur réseau : un écran d'agenda vide vaut mieux qu'un
 * écran en erreur pour un contenu d'agrément.
 */
export async function fetchPublicEvents(
  opts: { limit?: number; includePast?: boolean } = {},
): Promise<PublicEvent[]> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.includePast) params.set('includePast', '1');
  const qs = params.toString();
  try {
    const res = await apiFetch(
      `/events/public/${encodeURIComponent(TENANT_ID)}${qs ? `?${qs}` : ''}`,
      { auth: false },
    );
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body?.events) ? (body.events as PublicEvent[]) : [];
  } catch {
    return [];
  }
}

/** Détail public d'un événement. `null` = introuvable (ou réseau KO). */
export async function fetchPublicEvent(
  id: string,
): Promise<{ event: PublicEvent; tenant: PublicEventTenant | null } | null> {
  try {
    const res = await apiFetch(
      `/events/public/${encodeURIComponent(TENANT_ID)}/${encodeURIComponent(id)}`,
      { auth: false },
    );
    if (!res.ok) return null;
    const body = await res.json();
    return body?.event ? { event: body.event as PublicEvent, tenant: body.tenant ?? null } : null;
  } catch {
    return null;
  }
}

/**
 * URL à partager pour un événement.
 *
 * Pointe sur le backend, PAS sur la PWA : l'app est une SPA Vite sans SSR, dont
 * l'index.html porte les mêmes balises Open Graph pour toutes les routes. Le
 * backend sert une page par événement avec ses vraies métas (titre, date,
 * vignette), et redirige les humains vers la PWA. Cf. backend routes/og.ts.
 */
export function eventShareUrl(id: string): string {
  return `${BACKEND_URL}/e/${encodeURIComponent(TENANT_ID)}/${encodeURIComponent(id)}`;
}

// ── Formatage ────────────────────────────────────────────────────────────────

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

/**
 * Date 'YYYY-MM-DD' → objet Date local à MIDI. Le midi évite le glissement d'un
 * jour quand le fuseau décale un minuit (bug classique du `new Date('2026-08-22')`
 * interprété en UTC puis rendu en heure locale).
 */
export function parseEventDate(date: string): Date {
  return new Date(`${date}T12:00:00`);
}

/** « Août 2026 » — clé et libellé de regroupement de l'agenda. */
export function monthLabel(date: string): string {
  const d = parseEventDate(date);
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** « samedi 22 août » — sans l'année, qui vit dans l'en-tête de mois. */
export function formatDayLong(date: string): string {
  return parseEventDate(date).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** « 22 août 2026 » — date complète, pour la page événement. */
export function formatDateFull(date: string): string {
  return parseEventDate(date).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Pastille date d'une card : { day: '22', month: 'AOÛT' }. */
export function dateBadge(date: string): { day: string; month: string } {
  const d = parseEventDate(date);
  return {
    day: String(d.getDate()),
    month: d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '').toUpperCase(),
  };
}

/** '19:00' → '19h', '19:30' → '19h30'. */
export function formatTime(hm: string): string {
  const [h, m] = hm.split(':');
  return m === '00' ? `${Number(h)}h` : `${Number(h)}h${m}`;
}

/** Créneau lisible : « 19h – 23h », « à partir de 19h », ou null si pas d'heure. */
export function formatTimeRange(start: string | null, end: string | null): string | null {
  if (!start) return null;
  return end ? `${formatTime(start)} – ${formatTime(end)}` : `à partir de ${formatTime(start)}`;
}

/**
 * Prix affiché. Décision produit : le prix est INFORMATIF, il n'y a aucun
 * paiement dans cette phase. `null` ou 0 → « Entrée libre ».
 */
export function formatPrice(cents: number | null | undefined): string {
  if (cents == null || cents === 0) return 'Entrée libre';
  const euros = cents / 100;
  return euros % 1 === 0
    ? `${euros} €`
    : euros.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** Regroupe une liste triée par mois, en préservant l'ordre. */
export function groupByMonth(events: PublicEvent[]): Array<{ label: string; events: PublicEvent[] }> {
  const groups: Array<{ label: string; events: PublicEvent[] }> = [];
  for (const evt of events) {
    const label = monthLabel(evt.date);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.events.push(evt);
    else groups.push({ label, events: [evt] });
  }
  return groups;
}
